import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Image, Video, Clock, Star, CheckCircle, Heart, Trophy, Target, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import { useProgress } from '../contexts/ProgressContext.js';
import LoadingSpinner from '../components/common/LoadingSpinner.js';
import ExerciseDemonstration from '../components/ExerciseDemonstration.js';
import WorkoutOfTheWeek from '../components/WorkoutOfTheWeek.js';
import DailyChallenge from '../components/DailyChallenge.js';
import OptimizedImage from '../components/common/OptimizedImage.js';
import pdfDownloadService from '../services/pdfDownloadService.js';
import { toast } from 'react-toastify';
import CommentSection from '../components/comments/CommentSection.js';

const MonthlyPackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { workoutHistory } = useProgress();
  
  const [monthlyPackage, setMonthlyPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [showExerciseDemo, setShowExerciseDemo] = useState(false);
  const [rating, setRating] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [selectedExerciseVideo, setSelectedExerciseVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentVideoTitle, setCurrentVideoTitle] = useState('');

  const userTier = subscription?.tier || 'basic';

  // Get the API base URL
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';

  // Calculate challenge progress
  const getChallengeProgress = () => {
    if (!workoutHistory || !Array.isArray(workoutHistory) || !monthlyPackage?.subscriptionMonth) {
      return { completed: 0, total: 7, percentage: 0 };
    }

    const month = monthlyPackage.subscriptionMonth;
    const challengeDays = workoutHistory
      .filter(workout => {
        if (!workout.id) return false;
        
        // For month 1, match both old format (challenge-day-X) and new format (month1-challenge-day-X)
        // For months 2+, only match new format (monthX-challenge-day-X)
        if (month === 1) {
          return workout.id.includes(`month1-challenge-day-`) || 
                 (workout.id.includes('challenge-day-') && !workout.id.includes('month'));
        } else {
          return workout.id.includes(`month${month}-challenge-day-`);
        }
      })
      .map(workout => {
        // Extract day number from workout ID
        // New format: "month3-challenge-day-1" -> 1
        // Old format (month 1 only): "challenge-day-1" -> 1
        let dayMatch;
        if (workout.id.includes(`month${month}-challenge-day-`)) {
          dayMatch = workout.id.match(new RegExp(`month${month}-challenge-day-(\\d+)`));
        } else if (month === 1 && workout.id.includes('challenge-day-') && !workout.id.includes('month')) {
          // Legacy format - only for month 1
          dayMatch = workout.id.match(/challenge-day-(\d+)/);
        }
        return dayMatch ? parseInt(dayMatch[1]) : null;
      })
      .filter(day => day !== null);

    const completed = challengeDays.length;
    const total = 7;
    const percentage = Math.round((completed / total) * 100);

    return { completed, total, percentage };
  };

  // Recalculate challenge progress when monthlyPackage or workoutHistory changes
  const challengeProgress = useMemo(() => {
    return getChallengeProgress();
  }, [monthlyPackage, workoutHistory]);

  useEffect(() => {
    fetchPackage();
  }, [id]);

  // Extract initial day/week from URL hash
  const getInitialDayFromHash = () => {
    const hash = window.location.hash;
    if (hash.includes('challenge-day-')) {
      const dayMatch = hash.match(/challenge-day-(\d+)/);
      return dayMatch ? parseInt(dayMatch[1]) : null;
    }
    return null;
  };

  const getInitialWeekFromHash = () => {
    const hash = window.location.hash;
    if (hash.includes('wow-week-')) {
      const weekMatch = hash.match(/wow-week-(\d+)/);
      return weekMatch ? parseInt(weekMatch[1]) : null;
    }
    return null;
  };

  // Handle anchor scrolling when page loads
  useEffect(() => {
    const handleAnchorScroll = () => {
      const hash = window.location.hash;
      if (hash) {
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 1500); // Increased wait time to ensure content is rendered and day/week is selected
      }
    };

    // Check for anchor on page load
    if (monthlyPackage) {
      handleAnchorScroll();
    }

    // Listen for hash changes
    window.addEventListener('hashchange', handleAnchorScroll);
    
    return () => {
      window.removeEventListener('hashchange', handleAnchorScroll);
    };
  }, [monthlyPackage]);

  const fetchPackage = async () => {
    try {
      setLoading(true);
      console.log('Fetching package from:', `${apiBaseUrl}/monthly-packages/${id}`);
      const response = await fetch(`${apiBaseUrl}/monthly-packages/${id}?t=${Date.now()}&cache=bust`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch package');
      }

      const data = await response.json();
      console.log('MonthlyPackageDetail received data:', data);
      console.log('MonthlyPackageDetail workoutOfTheWeek:', data.workoutOfTheWeek);
      console.log('MonthlyPackageDetail dailyChallenge:', data.dailyChallenge);
      console.log('MonthlyPackageDetail title:', data.title);
      console.log('MonthlyPackageDetail theme:', data.theme);
      setMonthlyPackage(data);
    } catch (err) {
      console.error('Error fetching package:', err);
      setError('Failed to load package details');
    } finally {
      setLoading(false);
    }
  };

  const handleExerciseClick = (exercise) => {
    setSelectedExercise(exercise);
    setShowExerciseDemo(true);
  };

  const handleVideoPlay = (videoUrl, item) => {
    setCurrentVideoUrl(videoUrl);
    // Handle both workout objects (with title) and exercise objects (with title and movement)
    const title = item.title + (item.movement ? ` - ${item.movement}` : ``);
    setCurrentVideoTitle(title);
    setShowVideoModal(true);
  };

  const handleComplete = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/monthly-packages/${id}/complete?t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setIsCompleted(true);
        // Update the package to reflect completion
        setMonthlyPackage(prev => ({
          ...prev,
          analytics: {
            ...prev.analytics,
            completions: (prev.analytics?.completions || 0) + 1
          }
        }));
      }
    } catch (err) {
      console.error('Error marking package as complete:', err);
    }
  };

  const handleRating = async (newRating) => {
    try {
      const response = await fetch(`${apiBaseUrl}/monthly-packages/${id}/rate?t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rating: newRating })
      });

      if (response.ok) {
        setRating(newRating);
        // Update the package to reflect the new rating
        const data = await response.json();
        setMonthlyPackage(prev => ({
          ...prev,
          analytics: data.analytics
        }));
      }
    } catch (err) {
      console.error('Error rating package:', err);
    }
  };

  const getVideoUrl = (vimeoId) => {
    return `https://player.vimeo.com/video/${vimeoId}?autoplay=0&controls=1&pip=0&preload=metadata&poster=auto&watermark=0&logo=0&title=0&byline=0&portrait=0`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !monthlyPackage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Package Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The requested package could not be found.'}</p>
          <button
            onClick={() => navigate('/monthly-packages')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Packages
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/monthly-packages')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Packages
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div id="challenges" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl font-bold mb-4">{monthlyPackage.title}</h1>
              <p className="text-xl text-blue-100 mb-6">{monthlyPackage.description}</p>
              
              <div className="flex items-center space-x-6 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {monthlyPackage.dailyChallenge?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Daily Exercises</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {monthlyPackage.workoutOfTheWeek?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Weekly Workouts</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {monthlyPackage.estimatedDuration}
                  </div>
                  <div className="text-sm text-gray-600">Minutes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {monthlyPackage.analytics?.completions || 0}
                  </div>
                  <div className="text-sm text-gray-600">Completions</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-100">Challenge Progress</span>
                  <span className="text-sm text-blue-200">{challengeProgress.completed}/{challengeProgress.total} days completed</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-3">
                  <div 
                    className="bg-white h-3 rounded-full transition-all duration-500"
                    style={{ width: `${challengeProgress.percentage}%` }}
                  ></div>
                </div>
                <div className="mt-2 text-center">
                  <span className="text-sm text-blue-200">
                    {challengeProgress.completed === challengeProgress.total 
                      ? "🎉 Challenge Complete! You've mastered the fundamentals!"
                      : `Complete all ${challengeProgress.total} daily exercises to master fundamental movement patterns`
                    }
                  </span>
                </div>
              </div>

              {/* Rating */}
              {monthlyPackage.analytics?.averageRating > 0 && (
                <div className="flex items-center mb-6">
                  <div className="flex items-center mr-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.round(monthlyPackage.analytics.averageRating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-gray-600">
                    {monthlyPackage.analytics.averageRating.toFixed(1)} ({monthlyPackage.analytics.totalRatings} ratings)
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleComplete}
                  disabled={isCompleted}
                  className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                    isCompleted
                      ? 'bg-green-100 text-green-800 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isCompleted ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Completed
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Mark as Completed
                    </>
                  )}
                </button>

                {/* Rating Stars */}
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handleRating(i + 1)}
                      className={`w-6 h-6 ${
                        i < rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300 hover:text-yellow-400'
                      }`}
                    >
                      <Star className="w-full h-full" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Month 1 Premium Ebook - Presented like Basic Fit Test */}
        {monthlyPackage.subscriptionMonth === 1 && (
          <div className="mb-12" id="premium-ebook">
            <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">Premium Ebook: Back to You</h2>
            <div className="max-w-2xl mx-auto mb-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="relative">
                  <div className="aspect-[3/4] bg-white relative">
                    <img
                      src="https://res.cloudinary.com/dpy2y99bz/image/upload/f_auto,q_auto/png2pdf_6_-compressed_s3mzpx.jpg"
                      alt="Back to You - ElderFit Premium Ebook"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Back to You</h3>
                  <p className="text-gray-600 mb-4">This isn't a reset. This is a takeover. Step back into your power, own your routine, and run your fall like a CEO.</p>
                  <div className="flex justify-center">
                    <button
                      onClick={async () => {
                        try {
                          toast.info('Starting download...');
                          await pdfDownloadService.downloadPdf('png2pdf_6_-compressed_s3mzpx', 'elderfit-back-to-you-month-1.pdf');
                          toast.success('Download started');
                        } catch (e) {
                          toast.error(e.message || 'Failed to download ebook');
                        }
                      }}
                      className="px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                    >
                      Download Ebook
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Month 2 Premium Ebook - Presented like Basic Fit Test */}
        {monthlyPackage.subscriptionMonth === 2 && (
          <div className="mb-12" id="premium-ebook">
            <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">Premium Ebook: Fight For It</h2>
            <div className="max-w-2xl mx-auto mb-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="relative">
                  <div className="aspect-[3/4] bg-white relative">
                    <img
                      src="https://res.cloudinary.com/dpy2y99bz/image/upload/f_auto,q_auto/png2pdf_3_-compressed_cuzb3k.jpg"
                      alt="Fight For It - ElderFit Premium Ebook"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Fight For It</h3>
                  <p className="text-gray-600 mb-4">Mindset and momentum—build resilience with a focused, senior-friendly approach.</p>
                  <div className="flex justify-center">
                    <button
                      onClick={async () => {
                        try {
                          toast.info('Starting download...');
                          await pdfDownloadService.downloadPdf('png2pdf_3_-compressed_cuzb3k', 'elderfit-fight-for-it-month-2.pdf');
                          toast.success('Download started');
                        } catch (e) {
                          toast.error(e.message || 'Failed to download ebook');
                        }
                      }}
                      className="px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                    >
                      Download Ebook
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Month 3 Premium Ebook */}
        {monthlyPackage.subscriptionMonth === 3 && (
          <div className="mb-12" id="premium-ebook">
            <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">Premium Ebook: Gratitude + Grit</h2>
            <div className="max-w-2xl mx-auto mb-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="relative">
                  <div className="aspect-[3/4] bg-white relative">
                    <img
                      src="https://res.cloudinary.com/dpy2y99bz/image/upload/f_auto,q_auto/v1762766796/1_ecx5qi.png"
                      alt="Gratitude + Grit - ElderFit Premium Ebook"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Gratitude + Grit</h3>
                  <p className="text-gray-600 mb-4">How thankfulness builds strength, stamina, and success—equip your Month 3 journey with mindset tools that keep you grounded and going.</p>
                  <div className="flex justify-center">
                    <button
                      onClick={async () => {
                        try {
                          toast.info('Starting download...');
                          await pdfDownloadService.downloadPdf('Grit_and_Gratitude_e-book_branded_-compressed_m9wbmc', 'elderfit-grit-and-gratitude-month-3.pdf');
                          toast.success('Download started');
                        } catch (e) {
                          toast.error(e.message || 'Failed to download ebook');
                        }
                      }}
                      className="px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                    >
                      Download Ebook
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Daily Challenge Section - SEPARATE COMPONENT */}
        <div className="mb-12">
          <DailyChallenge 
            monthlyPackage={monthlyPackage} 
            onVideoPlay={handleVideoPlay} 
            initialDay={getInitialDayFromHash()}
          />
        </div>


        {/* Sidebar */}
        <div className="mb-12">
          <div className="space-y-6">
            {/* Full Follow-Along Video (Elite Only) */}
            {userTier === 'elite' && monthlyPackage.eliteVideo && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Full Follow-Along Workout</h3>
                <div className="border rounded-lg overflow-hidden">
                  <iframe
                    src={getVideoUrl(monthlyPackage.eliteVideo.vimeoId)}
                    width="100%"
                    height="200"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={monthlyPackage.eliteVideo.title}
                  />
                </div>
              </div>
            )}

            {/* Focus Areas */}
            {monthlyPackage.focusAreas?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Focus Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {monthlyPackage.focusAreas.map((area, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment */}
            {monthlyPackage.equipment?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Equipment Needed</h3>
                <div className="flex flex-wrap gap-2">
                  {monthlyPackage.equipment.map((item, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workout of the Week Section - SEPARATE COMPONENT */}
        {console.log('MonthlyPackageDetail: Checking if WorkoutOfTheWeek should render:', monthlyPackage.workoutOfTheWeek?.length > 0, 'workoutOfTheWeek length:', monthlyPackage.workoutOfTheWeek?.length)}
        {monthlyPackage.workoutOfTheWeek?.length > 0 && (
          <div id="wow" className="mb-12">
            {console.log('MonthlyPackageDetail: Rendering WorkoutOfTheWeek with data:', monthlyPackage)}
            <WorkoutOfTheWeek 
              monthlyPackage={monthlyPackage} 
              onVideoPlay={handleVideoPlay}
              initialWeek={getInitialWeekFromHash()}
            />
          </div>
        )}

        {/* Comments Section */}
        <div id="comments" className="mb-12">
          <CommentSection 
            targetType="monthlyPackage" 
            targetId={monthlyPackage._id || id}
          />
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{currentVideoTitle}</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <iframe
                src={currentVideoUrl}
                width="100%"
                height="400"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={currentVideoTitle}
              />
            </div>
          </div>
        </div>
      )}

      {/* Exercise Demo Modal */}
      {showExerciseDemo && selectedExercise && (
        <ExerciseDemonstration
          exercise={selectedExercise}
          onClose={() => setShowExerciseDemo(false)}
        />
      )}
    </div>
  );
};

export default MonthlyPackageDetail;

